import { Brand, BrandList, Location, Tag } from "../types/mapData";
import {
  buildMarkersFromLocations,
  createOrUpdateMarkerCluster,
} from "./markers";
import { buildTagsLookup } from "./tags";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

declare global {
  interface Window {
    currentMarkerCluster?: MarkerClusterer;
  }
}

interface HydrateOptions {
  data: {
    brands: Brand[];
    locations: Location[];
    tags: Tag[];
  };
  showDeliveryOnly: boolean;
  queryParams: Record<string, any>;
  map: google.maps.Map;
  oval: string;
  setLocations: (locations: Location[]) => void;
  setFilteredLocations: (locations: Location[]) => void;
  setBrands: (brands: Record<number, Brand>) => void;
  setInitLocations: (locations: Location[]) => void;
  setHiddenLength: (length: number) => void;
  setMarkersState: (markers: google.maps.Marker[]) => void;
  setTags: (tags: Record<number, Tag>) => void;
  handleLocationParam: (
    locations: Location[],
    brands: Record<number, Brand>,
  ) => void;
}

/**
 * Hydrates brands, locations, tags, and markers from fetched API data.
 *
 * Flow:
 *  1. Brand lookup
 *  2. Locations filtering
 *  3. Tag lookup
 *  4. Marker creation & clustering
 *  5. Deep link handling (optional location param)
 *
 * Notes:
 *  - If `showDeliveryOnly`, filters to delivery tag id (10000)
 *  - If `queryParams.tagId` exists, filters by that tag
 *  - Otherwise loads all locations
 */
export function hydrateLocationsAndBrands({
  data,
  showDeliveryOnly,
  queryParams,
  map,
  oval,
  setBrands,
  setInitLocations,
  setLocations,
  setFilteredLocations,
  setHiddenLength,
  setMarkersState,
  setTags,
  handleLocationParam,
}: HydrateOptions) {
  if (!data) {
    console.warn("hydrateLocationsAndBrands: no data received");
    return;
  }

  // ─── 1. Brand Hydration ─────────────────────────────────────
  const brandsList: Record<number, Brand> = {};
  data.brands.forEach((brand) => {
    brandsList[brand.brand_id] = brand;
  });
  setBrands(brandsList);

  // ─── 2. Location Filtering ──────────────────────────────────
  const deliveryTagId = 10000;
  let filterTagId: number | null = null;

  if (showDeliveryOnly) {
    filterTagId = deliveryTagId;
  } else if (queryParams.tagId) {
    filterTagId = Number(queryParams.tagId);
  }

  const allLocations = data.locations || [];
  const filteredLocations = filterTagId
    ? allLocations.filter((loc) =>
        brandsList?.[loc.brand_id]?.tags?.some((tag) => tag.id === filterTagId),
      )
    : allLocations;

  setInitLocations(filteredLocations);
  setLocations(filteredLocations);
  setFilteredLocations(filteredLocations);
  setHiddenLength(Math.max(0, data.brands.length - 15));

  // ─── 3. Tag Hydration ───────────────────────────────────────
  const tagsLookup = buildTagsLookup(data.tags);
  setTags(tagsLookup);

  // ─── 4. Marker Hydration ─────────────────────────────────────
  if (map) {
    const markers = buildMarkersFromLocations(
      filteredLocations,
      map,
      oval,
      true,
    );
    createOrUpdateMarkerCluster({ map, markers, ovalUrl: oval });
    setMarkersState(markers);
  } else {
    console.warn("❌ No map instance available when building markers");
  }

  // ─── 5. Handle Deep Link Location (Optional) ─────────────────
  if (queryParams?.location) {
    handleLocationParam(filteredLocations, brandsList);
  }
}

export const filterLocationsByBrandAndDelivery = (
  locations: Location[],
  brandsList: BrandList,
  brandSlug: string,
  showDeliveryOnly: boolean,
): Location[] => {
  return locations.filter((loc) => {
    const matchesBrand = !brandSlug || loc?.brand_slug === brandSlug;

    const supportsDelivery =
      !showDeliveryOnly ||
      brandsList?.[loc.brand_id]?.tags?.some((tag) => tag.id === 10000);

    return matchesBrand && supportsDelivery;
  });
};
